/**
 * Shared bits for the AI Tutor interactive test pages (Tests, TestEditor, TestGradeRules, TestAttempts).
 * API: ryd-learning-api-v2/controllers/controller.ai_test.js
 */
import React, { useEffect, useState } from "react"
import moment from "moment"
import axios from "axios"
import { baseUrl } from "../../Network"
import { handleAuthError } from "../../utils"

export const QUESTION_TYPES = [
  { value: "code_prediction", label: "Code prediction", pill: "Predict the output" },
  { value: "multiple_choice", label: "Multiple choice", pill: "Choose the answer" },
  { value: "true_false", label: "True or false", pill: "True or false" },
  { value: "fill_blank", label: "Fill in the blank", pill: "Fill in the blank" },
  { value: "match_pairs", label: "Match the pairs", pill: "Match the pairs" },
]

export const typeLabel = (type) => QUESTION_TYPES.find((t) => t.value === type)?.label || type

export const LEVELS = ["Beginner", "Intermediate", "Advanced"]

export const RULE_ACTIONS = [
  { value: "next_level", label: "Move to the next level" },
  { value: "next_course", label: "Recommend a specific course" },
  { value: "review_lessons", label: "Review the lessons they missed" },
  { value: "retake_test", label: "Retake the test" },
  { value: "learning_path", label: "Recommend a learning path" },
  { value: "none", label: "Message only" },
]

export const actionLabel = (action) => RULE_ACTIONS.find((a) => a.value === action)?.label || action

export const ACCENT = "#C026D3"

/** Message from an API error (Joi messages come back as 400s). */
export const apiErrorMessage = (e, fallback = "Something went wrong") =>
  e?.response?.data?.message || e?.message || fallback

export const formatDateTime = (v) => (v ? moment(v).format("DD MMM YYYY, HH:mm") : "—")

export const formatDuration = (seconds) => {
  if (seconds == null) return "—"
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m ? `${m}m ${s}s` : `${s}s`
}

const idOrNull = (v) => (v === "" || v == null ? null : Number(v))

/** The fields the API accepts for a test (strips ids, timestamps, counts). */
export const testPayload = (t) => ({
  title: String(t.title || "").trim(),
  description: t.description || "",
  curriculumId: idOrNull(t.curriculumId),
  language: t.language || "",
  level: t.level || "",
  timeLimitMinutes: idOrNull(t.timeLimitMinutes),
  passMark: t.passMark === "" || t.passMark == null ? 50 : Number(t.passMark),
  maxAttempts: idOrNull(t.maxAttempts),
  instantFeedback: t.instantFeedback !== false,
  shuffleQuestions: !!t.shuffleQuestions,
  subcategoryId: idOrNull(t.subcategoryId),
  sortOrder: t.sortOrder === "" || t.sortOrder == null ? 0 : Number(t.sortOrder),
  passRecommendCurriculumId: idOrNull(t.passRecommendCurriculumId),
  passRecommendTestId: idOrNull(t.passRecommendTestId),
  failRecommendCurriculumId: idOrNull(t.failRecommendCurriculumId),
})

/**
 * The test part of an export file. Courses and the category are named by slug, because ids differ between
 * servers. A recommended test has no slug, so it keeps its id; the import leaves it out if it isn't there.
 */
const EXPORT_DROPS = ["curriculumId", "subcategoryId", "passRecommendCurriculumId", "failRecommendCurriculumId"]

const exportTest = (t) => {
  const rest = { ...testPayload(t) }
  EXPORT_DROPS.forEach((key) => delete rest[key])
  return {
    ...rest,
    courseSlug: t.curriculum?.slug || t.courseSlug || null,
    categorySlug: t.subcategory?.category?.slug || null,
    subcategorySlug: t.subcategory?.slug || null,
    passRecommendCourseSlug: t.passRecommendCourseSlug || null,
    failRecommendCourseSlug: t.failRecommendCourseSlug || null,
  }
}

/** Courses, test categories (the learning-path taxonomy) and tests for the test settings form. */
export const useTestFormOptions = () => {
  const [options, setOptions] = useState({ curricula: [], categories: [], tests: [] })
  useEffect(() => {
    let cancelled = false
    const list = (url) =>
      axios
        .get(`${baseUrl}${url}`)
        .then((res) => (Array.isArray(res?.data?.data) ? res.data.data : []))
        .catch((e) => {
          handleAuthError(e)
          return []
        })
    Promise.all([list("/admin/curriculum/all"), list("/admin/learning-path/category/all"), list("/admin/ai-test/all")]).then(
      ([curricula, categories, tests]) => {
        if (!cancelled) setOptions({ curricula, categories, tests })
      }
    )
    return () => {
      cancelled = true
    }
  }, [])
  return options
}

/** The fields the API accepts for a question. */
export const questionPayload = (q) => ({
  type: q.type,
  prompt: q.prompt,
  code: q.code || "",
  payload: q.payload,
  explanation: q.explanation || "",
  points: Number(q.points) || 1,
  lessonRef: q.lessonRef && q.lessonRef.courseSlug ? q.lessonRef : null,
})

/** { test, questions } — the shape POST /admin/ai-test/import accepts. */
export const exportShape = (test) => ({
  test: exportTest(test),
  questions: (test.questions || []).map(questionPayload),
})

/**
 * Downloadable example for "Import JSON": one card of every type, in the shape POST /admin/ai-test/import accepts.
 * The API ignores unknown keys, so the "_help" notes can stay in the file.
 */
export const SAMPLE_TEST_IMPORT = {
  _help: [
    "Edit this file, then use Import JSON on the Manage Tests page. The test is created as a draft.",
    "test.courseSlug: the course slug from Manage Curriculum (e.g. python-basics), or null for a standalone test.",
    "test.categorySlug and test.subcategorySlug: the category and subcategory slugs from Learning path categories, or null.",
    "test.sortOrder: where the test appears for learners; lower numbers come first.",
    "test.passRecommendCourseSlug or test.passRecommendTestId: what to suggest after a pass (one of the two, or both null).",
    "test.failRecommendCourseSlug: the course to suggest after a fail, or null. A fail never suggests a test.",
    "Anything the server can't find (course, category, test) is left out, and the import tells you what was skipped.",
    "test.level: Beginner, Intermediate or Advanced. timeLimitMinutes and maxAttempts: a number, or null for none.",
    "test.instantFeedback: true shows right or wrong after each card; false is exam mode.",
    "Card types: code_prediction, multiple_choice, true_false, fill_blank, match_pairs.",
    "code_prediction / multiple_choice: 2 to 6 options with ids a to f; answer is the id of the right option. code_prediction also needs code.",
    "true_false: answer is true or false. fill_blank: answer must be one of the options (2 to 8 word chips).",
    "match_pairs: 2 to 6 pairs; learners see the right side shuffled.",
    "lessonRef (optional): { courseSlug, lessonId, lessonTitle } of the lesson to review when the card is missed, or null.",
  ],
  test: {
    title: "Python basics check",
    description: "Five quick cards on printing, variables and loops.",
    courseSlug: null,
    categorySlug: null,
    subcategorySlug: null,
    sortOrder: 0,
    language: "python",
    level: "Beginner",
    timeLimitMinutes: null,
    passMark: 60,
    maxAttempts: 3,
    instantFeedback: true,
    shuffleQuestions: false,
    passRecommendCourseSlug: null,
    passRecommendTestId: null,
    failRecommendCourseSlug: null,
  },
  questions: [
    {
      type: "code_prediction",
      prompt: "What do you think this code will do?",
      code: 'print("Hello, World!")',
      payload: {
        options: [
          { id: "a", label: 'Prints "Hello, World!" to the screen' },
          { id: "b", label: "Saves the text to a file" },
          { id: "c", label: "Asks the user a question" },
          { id: "d", label: "Does nothing until run twice" },
        ],
        answer: "a",
      },
      explanation: "print() sends text straight to the output — no file, no waiting.",
      points: 1,
      lessonRef: null,
    },
    {
      type: "true_false",
      prompt: "A for loop can only ever run 3 times.",
      code: "",
      payload: { answer: false },
      explanation: "A for loop runs as many times as you tell it to — range(3) is just one example.",
      points: 1,
      lessonRef: null,
    },
    {
      type: "fill_blank",
      prompt: "Complete the code so it prints the word Hi!",
      code: "",
      payload: { pre: "print(", post: ")", options: ['"Hi!"', "Hi!", "print", "5"], answer: '"Hi!"' },
      explanation: "Text needs quotes around it — that is what makes it a string.",
      points: 1,
      lessonRef: null,
    },
    {
      type: "match_pairs",
      prompt: "Match each line of code to what it does",
      code: "",
      payload: {
        pairs: [
          { id: "p1", left: 'print("hi")', right: "Displays text on the screen" },
          { id: "p2", left: "x = 5", right: "Stores a value in a variable" },
          { id: "p3", left: "if x > 3:", right: "Checks a condition before running code" },
          { id: "p4", left: "for i in range(3):", right: "Repeats a block of code several times" },
        ],
      },
      explanation: "These four lines are the building blocks of most programs.",
      points: 2,
      lessonRef: null,
    },
    {
      type: "multiple_choice",
      prompt: "Which line stores the number 10 in a variable?",
      code: "",
      payload: {
        options: [
          { id: "a", label: "x = 10" },
          { id: "b", label: "x == 10" },
          { id: "c", label: "print(10)" },
        ],
        answer: "a",
      },
      explanation: "A single = stores a value; == compares two values.",
      points: 1,
      lessonRef: null,
    },
  ],
}

export const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const slugifyFile = (s) =>
  String(s || "test")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "test"

/** Lessons ({ id, title }) from a curriculum row; handles v1 and v2 and either nesting of the JSON blob. */
export const lessonsFromCurriculum = (row) => {
  let blob = row?.curriculum
  if (typeof blob === "string") {
    try {
      blob = JSON.parse(blob)
    } catch {
      blob = null
    }
  }
  if (!blob || typeof blob !== "object") return []
  const modules = Array.isArray(blob.modules)
    ? blob.modules
    : Array.isArray(blob.curriculum?.modules)
      ? blob.curriculum.modules
      : []
  const out = []
  modules.forEach((m, mi) => {
    ;(Array.isArray(m?.lessons) ? m.lessons : []).forEach((l) => {
      if (!l || l.id == null) return
      out.push({ id: String(l.id), title: l.title || String(l.id), moduleTitle: m.title || `Module ${mi + 1}` })
    })
  })
  return out
}

/** Readable form of an answer value for review tables. */
export const describeAnswer = (question, value) => {
  if (value == null) return "—"
  if (question?.type === "true_false") return value === true ? "True" : value === false ? "False" : String(value)
  if (question?.type === "code_prediction" || question?.type === "multiple_choice") {
    const opt = (question.options || []).find((o) => String(o.id) === String(value))
    return opt ? `${String(opt.id).toUpperCase()}. ${opt.label}` : String(value)
  }
  if (question?.type === "match_pairs") {
    const matches = value.matches || value
    if (!matches || typeof matches !== "object") return "—"
    const right = new Map((question.right || []).map((r) => [r.token, r.text]))
    return (question.left || [])
      .map((l) => `${l.text} → ${right.get(matches[l.id]) || "?"}`)
      .join("; ")
  }
  return String(value)
}

const cardStyle = {
  background: "#FBF5FC",
  borderRadius: 20,
  padding: 16,
}

const innerStyle = {
  background: "#FFFFFF",
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 16px 40px -24px rgba(154,52,163,0.35)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
}

const pillStyle = {
  alignSelf: "flex-start",
  background: "#F6E9FB",
  color: ACCENT,
  padding: "4px 10px",
  borderRadius: 999,
  fontWeight: 700,
  fontSize: 11,
}

const codeBoxStyle = {
  background: "#1E1B2E",
  color: "#E9E4F0",
  borderRadius: 12,
  padding: "12px 14px",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: 13,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}

const optionRowStyle = (correct) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 12,
  border: `2px solid ${correct ? "#16A34A" : "#ECE6F0"}`,
  background: correct ? "#ECFDF3" : "#FFFFFF",
  color: correct ? "#15803D" : "#1F1B24",
  fontSize: 13,
  fontWeight: 600,
})

const letterStyle = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "#F3EEF7",
  color: "#6B6572",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: 11,
  flexShrink: 0,
}

const CodeBlock = ({ code }) => (
  <div style={codeBoxStyle}>
    <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF5F57" }} />
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FEBC2E" }} />
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#28C840" }} />
    </div>
    {code || " "}
  </div>
)

/**
 * A small, static picture of how the learner will see a card (correct answer highlighted),
 * styled after docs/Code Prediction Flashcard — RYD Learning.html.
 */
export const TestCardPreview = ({ question }) => {
  if (!question) return null
  const p = question.payload || {}
  const type = QUESTION_TYPES.find((t) => t.value === question.type)
  return (
    <div style={cardStyle}>
      <div style={innerStyle}>
        <span style={pillStyle}>{type?.pill || "Question"}</span>
        {question.type === "code_prediction" && <CodeBlock code={question.code} />}
        {question.type === "fill_blank" && (
          <div style={codeBoxStyle}>
            {p.pre || ""}
            <span
              style={{
                display: "inline-block",
                minWidth: 40,
                padding: "0 6px",
                margin: "0 2px",
                borderRadius: 6,
                background: "#16A34A",
                color: "#FFFFFF",
              }}
            >
              {p.answer || "____"}
            </span>
            {p.post || ""}
          </div>
        )}
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1F1B24" }}>{question.prompt || "Question text"}</div>

        {(question.type === "code_prediction" || question.type === "multiple_choice") && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(p.options || []).map((o) => (
              <div key={o.id} style={optionRowStyle(o.id === p.answer)}>
                <span style={letterStyle}>{String(o.id).toUpperCase()}</span>
                <span>{o.label || "…"}</span>
              </div>
            ))}
          </div>
        )}

        {question.type === "true_false" && (
          <div style={{ display: "flex", gap: 8 }}>
            {[true, false].map((v) => (
              <div key={String(v)} style={{ ...optionRowStyle(p.answer === v), flex: 1, justifyContent: "center" }}>
                {v ? "True" : "False"}
              </div>
            ))}
          </div>
        )}

        {question.type === "fill_blank" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(p.options || []).map((o, i) => (
              <span
                key={`${o}-${i}`}
                style={{
                  ...optionRowStyle(o === p.answer),
                  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                  padding: "4px 10px",
                }}
              >
                {o || "…"}
              </span>
            ))}
          </div>
        )}

        {question.type === "match_pairs" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {(p.pairs || []).map((pair) => (
              <React.Fragment key={pair.id}>
                <div style={{ ...codeBoxStyle, padding: "6px 10px", fontSize: 12 }}>{pair.left || "…"}</div>
                <div style={{ ...optionRowStyle(false), fontSize: 12 }}>{pair.right || "…"}</div>
              </React.Fragment>
            ))}
          </div>
        )}

        {question.explanation ? (
          <div style={{ background: "#ECFDF3", color: "#15803D", borderRadius: 12, padding: "8px 10px", fontSize: 12 }}>
            <strong>Nice — spot on!</strong> {question.explanation}
          </div>
        ) : null}
      </div>
    </div>
  )
}
